/**
 * Keeping this device and the hub in step.
 *
 * One round is: push what we have done, then pull what we have not seen. Push
 * first on purpose — a sale rung up during an outage is the thing most likely to
 * be lost, so it should leave the device at the first opportunity, before
 * anything else can go wrong with the connection.
 *
 * The loop is quiet by design. Sync failing is not an error a cashier can act
 * on: the shop keeps working either way, and interrupting a queue to report that
 * a background exchange did not happen would be worse than the outage.
 */

import type { AuthenticatedUser } from '@bakery/shared';

import { api } from '../lib/api.js';
import { localCommand, whenLocalDbReady } from './local-db.js';

export interface SyncRound {
  ran: boolean;
  reason?: string;
  pushed: number;
  pulled: number;
  /** Changes we sent that the hub discarded because it held something newer. */
  superseded: number;
  hubDeviceId?: string;
}

const CURSOR_PUSH = 'bakery.sync.pushedThrough';
const CURSOR_PULL = 'bakery.sync.pulledThrough';

function readCursor(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeCursor(key: string, value: string | null): void {
  try {
    if (value) localStorage.setItem(key, value);
  } catch {
    /* a device that cannot remember its cursor re-sends; harmless, just chattier */
  }
}

let inFlight: Promise<SyncRound> | null = null;

/**
 * One exchange. Never throws, and never runs twice at once.
 *
 * Overlapping rounds would push the same changes twice and race on the cursors,
 * so a second caller joins the round already running rather than starting one.
 */
export function syncOnce(user: AuthenticatedUser): Promise<SyncRound> {
  if (inFlight) return inFlight;
  inFlight = runRound(user).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function runRound(user: AuthenticatedUser): Promise<SyncRound> {
  const empty: SyncRound = { ran: false, pushed: 0, pulled: 0, superseded: 0 };

  const state = await whenLocalDbReady();
  if (!state.ready || !state.deviceId) {
    console.warn('Sync skipped: this device has no local database');
    return { ...empty, reason: 'this device has no local database' };
  }

  try {
    // --- Push ---------------------------------------------------------------
    const outgoing = await localCommand<{
      cursor: string | null;
      changes: unknown[];
      hasMore: boolean;
    }>('sync.collect', { since: readCursor(CURSOR_PUSH) }, user);

    let pushed = 0;
    let superseded = 0;
    let hubDeviceId: string | undefined;

    if (outgoing.changes.length > 0) {
      const receipt = await api.sync.push({
        fromDeviceId: state.deviceId,
        changes: outgoing.changes,
        cursor: outgoing.cursor,
      });
      pushed = receipt.applied;
      superseded = receipt.superseded;
      hubDeviceId = receipt.hubDeviceId;

      // Only advance after the hub confirms. A cursor moved on optimism is a
      // change that silently never arrives anywhere.
      writeCursor(CURSOR_PUSH, outgoing.cursor);
    }

    // --- Pull ---------------------------------------------------------------
    const incoming = await api.sync.pull({
      since: readCursor(CURSOR_PULL),
      deviceId: state.deviceId,
    });

    let pulled = 0;
    if (incoming.changes.length > 0) {
      const merged = await localCommand<{ applied: number }>(
        'sync.merge',
        { changes: incoming.changes, fromDeviceId: incoming.fromDeviceId },
        user,
      );
      pulled = merged.applied;
      writeCursor(CURSOR_PULL, incoming.cursor);
    } else if (incoming.cursor) {
      writeCursor(CURSOR_PULL, incoming.cursor);
    }

    /*
     * Logged, not silent. Sync failing is not something a cashier can act on,
     * so it must not interrupt them — but when the shop's figures look wrong,
     * "what has this device actually exchanged" is the first question, and an
     * exchange that leaves no trace anywhere is impossible to answer it with.
     */
    if (pushed || pulled || superseded) {
      console.info(`Sync: pushed ${pushed}, pulled ${pulled}${superseded ? `, ${superseded} superseded` : ''}`);
    }
    return { ran: true, pushed, pulled, superseded, hubDeviceId };
  } catch (error) {
    console.warn('Sync round failed:', (error as Error).message);
    return { ...empty, reason: (error as Error).message };
  }
}

let timer: ReturnType<typeof setInterval> | null = null;

/**
 * Sync on a timer, when the tab is brought back, and when the network returns.
 *
 * The browser's `online` event is the important one: it fires the moment wifi
 * comes back, which is exactly when a device is holding sales nobody else knows
 * about yet.
 */
export function startSyncLoop(user: AuthenticatedUser, everyMs = 60_000): () => void {
  const run = () => void syncOnce(user);

  run();
  timer = setInterval(run, everyMs);

  const onVisible = () => {
    if (document.visibilityState === 'visible') run();
  };
  window.addEventListener('online', run);
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    if (timer) clearInterval(timer);
    timer = null;
    window.removeEventListener('online', run);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
